import { Button } from '@heroui/react';
import { MinusSquareDashed } from 'iconoir-react';
import { parseAsJson, useQueryState } from 'nuqs';

import { DeploymentsGridView } from './DeploymentsGridView';
import { DeploymentsListView } from './DeploymentsListView';
import { useDeploymentsContext } from '../context/DeploymentsContext';
import { useDeployUiStore } from '../stores/deployUiStore';

import i18n from '~/common/helpers/i18n';

type DeploymentsViewRendererProps = {
  view: string;
};

function EmptyView({
  hasFilters,
  resetFilters,
  createNewDeploy
}: {
  hasFilters: boolean;
  resetFilters: () => void;
  createNewDeploy: () => void;
}) {
  return (
    <div className='flex flex-col gap-4 p-10'>
      <div className='flex items-center justify-center px-6 py-2'>
        <div className='rounded-lg bg-surface-container p-4'>
          <MinusSquareDashed className='h-12 w-12 text-on-surface' />
        </div>
      </div>

      <div className='flex flex-col items-center justify-center gap-6 text-center'>
        <div>
          <h2 className='mb-1 text-headline-md font-semibold text-on-surface'>
            {i18n.t('scenes.runtime.guidance.new_empty_list_title')}
          </h2>
          <p className='text-body-regular-md text-on-surface'>
            {i18n.t('scenes.runtime.guidance.new_empty_list_subtitle')}
          </p>
        </div>
        <div className='flex gap-1'>
          {hasFilters && (
            <Button
              variant='bordered'
              size='md'
              color='primary'
              className='h-8 border-1 text-sm font-semibold leading-tight tracking-tight'
              onPress={resetFilters}
            >
              {i18n.t('scenes.runtime.guidance.new_empty_list_button')}
            </Button>
          )}
          <Button
            size='md'
            color='primary'
            className='h-8 border-1 text-sm font-semibold leading-tight tracking-tight'
            onPress={createNewDeploy}
          >
            {i18n.t('scenes.runtime.guidance.new_empty_list_create_button')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DeploymentsViewRenderer({
  view
}: DeploymentsViewRendererProps) {
  const { loading, deploymentsList } = useDeploymentsContext();
  const setDeploySheetOpen = useDeployUiStore(
    state => state.setDeploySheetOpen
  );
  const [filters, setFilters] = useQueryState(
    'filters',
    parseAsJson<Record<string, string[]>>(
      v => v as Record<string, string[]>
    ).withDefault({})
  );
  const [search, setPipelineName] = useQueryState('search');
  const resetFilters = () => {
    setFilters(null);
    setPipelineName(null);
  };

  if (!loading && deploymentsList && !deploymentsList?.totalElements)
    return (
      <EmptyView
        hasFilters={Object.keys(filters).length > 0 || !!search}
        resetFilters={resetFilters}
        createNewDeploy={() => setDeploySheetOpen(true)}
      />
    );

  if (view === 'list') return <DeploymentsListView />;

  return <DeploymentsGridView />;
}
